import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    BoxProps,
    capitalize,
    Chip,
    ListItemIcon,
    ListItemProps,
    ListItemText,
    ListProps,
    Box as MuiBox,
    Button as MuiButton,
    List as MuiList,
    ListItem as MuiListItem,
    styled,
    Theme,
    Typography,
    useMediaQuery,
} from "@mui/material";
import {
    GridColDef,
    GridRowParams,
    DataGrid as MuiDataGrid,
} from "@mui/x-data-grid";
import dayjs from "dayjs";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetCategories } from "src/api/category";
import { useGetElementsPerMap } from "src/api/element";
import { FormContext } from "src/contexts/form";
import { LanguageContext } from "src/contexts/language";
import { MapContext } from "src/contexts/map";
import ElementDetails from "src/dialogs/ElementDetails";
import AddElement from "src/forms/AddElement";
import { Type_Category } from "src/types/category";
import { Type_Element_Data, Type_Formatted_Element } from "src/types/element";
import { getIconFromPicto } from "src/utils/category";
import {
    exportToXlsx,
    transformElementsToListStructure,
} from "src/utils/element";

import MobileSearchBar from "./mobile/MobileSearchBar";

export const DEFAULT_PAGE_SIZE = 10;

const Container = styled(MuiBox)<BoxProps>(() => ({
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
}));

const Box = styled(MuiBox)<BoxProps>(({ theme }) => ({
    marginTop: theme.spacing(4),
    marginLeft: theme.spacing(3),
    width: "98%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
}));

const DataGrid = styled(MuiDataGrid)(({ theme }) => ({
    width: "98%",
    border: "none",
    marginTop: theme.spacing(2),
    "& .MuiDataGrid-cell": {
        display: "flex",
        alignItems: "center",
    },
}));

const Button = styled(MuiButton)(({ theme }) => ({
    color: theme.palette.secondary.dark,
    borderColor: theme.palette.secondary.dark,
    "&:hover": {
        color: theme.palette.secondary.dark,
        borderColor: theme.palette.secondary.dark,
    },
}));

const List = styled(MuiList)<ListProps>(() => ({
    width: "100%",
}));

const ListItem = styled(MuiListItem)<ListItemProps>(() => ({
    cursor: "pointer",
    "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.1)",
    },
}));

const ListView: React.FC = () => {
    const {
        selectedMap,
        updateNewElement,
        updateElements,
        updateCategories,
        filters,
    } = useContext(MapContext);
    const { openForm } = useContext(FormContext);
    const { language } = useContext(LanguageContext);

    const { data: categories = [], isLoading: isCategoryLoading } =
        useGetCategories() || {};

    const { data: elements = [], isLoading: isElementLoading } =
        useGetElementsPerMap(selectedMap?.id, filters) || {};

    const [formatElements, setFormatElements] = useState<
        Type_Formatted_Element[]
    >([]);
    const [openDetails, setopenDetails] = useState(false);

    const { t } = useTranslation();

    const elementCategory = (element: Type_Element_Data): Type_Category =>
        categories.find(
            (category: Type_Category) => category.id === element.category_id,
        );

    const columns: GridColDef[] = [
        {
            field: "title",
            type: "string",
            flex: 1,
            renderCell: (params) => (
                <Typography>{capitalize(params.value || "")}</Typography>
            ),
            renderHeader: () => <strong>{t("addElement.event")}</strong>,
        },
        {
            field: "description",
            type: "string",
            flex: 3,
            renderCell: (params) => (
                <Typography color="text.secondary">
                    {capitalize(params.value || "")}
                </Typography>
            ),
            renderHeader: () => <strong>{t("addElement.description")}</strong>,
        },
        {
            field: "category",
            type: "string",
            flex: 1,
            renderHeader: () => <strong>{t("addElement.category")}</strong>,
            renderCell: (params) => (
                <Chip
                    sx={{
                        borderRadius: 1,
                        borderColor: elementCategory(params.row)?.style
                            ?.primary_color,
                        color: elementCategory(params.row)?.style
                            ?.primary_color,
                        justifyContent: "flex-start",
                    }}
                    label={elementCategory(params.row)?.title[language]}
                    variant="outlined"
                    icon={
                        <FontAwesomeIcon
                            color={
                                elementCategory(params.row)?.style
                                    ?.primary_color
                            }
                            icon={getIconFromPicto(
                                elementCategory(params.row)?.picto,
                            )}
                        />
                    }
                />
            ),
        },
        {
            field: "start_date",
            type: "date",
            renderHeader: () => <strong>{t("addElement.startDate")}</strong>,
            valueGetter: (params) => {
                const dayjsDate = dayjs(params);
                return dayjsDate.isValid() ? dayjsDate.toDate() : null;
            },
            renderCell: (params) => (
                <strong>{dayjs(params.value).format("L LT")}</strong>
            ),
            flex: 1,
        },
        {
            field: "end_date",
            type: "date",
            flex: 1,
            renderHeader: () => <strong>{t("addElement.endDate")}</strong>,
            valueGetter: (params) => {
                const dayjsDate = dayjs(params);
                return dayjsDate.isValid() ? dayjsDate.toDate() : null;
            },
            renderCell: (params) => (
                <strong>{dayjs(params.value).format("L LT")}</strong>
            ),
        },
    ];

    useEffect(() => {
        if (!isElementLoading && !isCategoryLoading) {
            updateCategories(categories);
            updateElements(elements);
            const formattedElements: Type_Formatted_Element[] =
                transformElementsToListStructure(
                    elements,
                    categories,
                    language,
                );
            setFormatElements(formattedElements);
        }
    }, [isElementLoading, isCategoryLoading, elements]);

    const isMobile = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down("md"),
    );

    const handleRowClick = (params: GridRowParams) => {
        updateNewElement(params.row);
        setopenDetails(!openDetails);
    };

    const handleItemClick = (params: Type_Element_Data) => {
        updateNewElement(params);
        setopenDetails(!openDetails);
    };

    const WebListView = () => (
        <>
            <Box>
                <Typography variant="h5">{t("events")}</Typography>
                <Button
                    variant="outlined"
                    startIcon={<FontAwesomeIcon icon={faDownload} />}
                    onClick={() => exportToXlsx(formatElements, t)}
                >
                    {t("export")}
                </Button>
            </Box>
            <DataGrid
                rows={formatElements}
                columns={columns}
                initialState={{
                    pagination: {
                        paginationModel: {
                            pageSize: DEFAULT_PAGE_SIZE,
                        },
                    },
                }}
                pageSizeOptions={[5, DEFAULT_PAGE_SIZE, 15]}
                onRowClick={handleRowClick}
                disableColumnMenu
            />
        </>
    );

    const MobileListView = () => (
        <>
            <MobileSearchBar />
            <List>
                {elements.map((element: Type_Element_Data, idx: number) => (
                    <ListItem
                        key={idx}
                        onClick={() => handleItemClick(element)}
                    >
                        <ListItemIcon>
                            <FontAwesomeIcon
                                color={
                                    elementCategory(element)?.style
                                        ?.primary_color
                                }
                                icon={getIconFromPicto(
                                    elementCategory(element).picto,
                                )}
                            />
                        </ListItemIcon>
                        <ListItemText
                            primary={element.title}
                            secondary={`${dayjs(element.start_date).format("L")} - ${dayjs(element.end_date).format("L")}`}
                        />
                    </ListItem>
                ))}
            </List>
        </>
    );

    if (isElementLoading || isCategoryLoading) return null;

    return (
        <Container>
            {!isMobile ? <WebListView /> : <MobileListView />}

            {openDetails && (
                <ElementDetails close={() => setopenDetails(false)} />
            )}

            {openForm && <AddElement />}
        </Container>
    );
};

export default ListView;
